import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { useLocation } from "react-router-dom";
import { stores } from "../context";
import { IType } from "../types/entities";
import { getLocalized } from "../utils/getLocalized";
import { useTranslation } from "react-i18next";

const MobileCategoryDrawer = observer(() => {
  const { product } = stores;
  const location = useLocation();
  const { t, i18n } = useTranslation("menu");
  const [open, setOpen] = useState(false);

  // En: Display the floating category button exclusively on the /menu page
  if (location.pathname !== "/menu") {
    return null;
  }

  // En: Filter product categories dynamically based on the active menu type (FOOD or BAR)
  const filteredCategories = (
    Array.isArray(product.types) ? product.types : []
  ).filter((cat: any) => {
    if (product.activeMenu === "BAR") {
      return Boolean(cat.isAlcohol);
    }
    return !cat.isAlcohol;
  });

  // En: Handle smooth scrolling to the selected category section element
  const handleScroll = (type: IType) => {
    // En: Match the exact ID calculation logic used inside MenuPage.tsx (`category-${type.id}`)
    const targetId = `category-${type.id}`;
    const el = document.getElementById(targetId);

    if (el) {
      const offset = 90; // En: Safe layout navigation offset height for sticky elements
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = el.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    } else {
      console.warn(
        `[MobileCategoryDrawer] Target content section element #${targetId} not found on this page.`
      );
    }
    setOpen(false);
  };

  return (
    <>
      {/* En: Floating categories trigger button positioned at the bottom-left of the mobile screen */}
      <button
        type="button"
        className="md:hidden fixed bottom-6 left-6 z-40 flex items-center gap-2 px-4 py-3 rounded-full bg-amber-600 text-stone-950 font-bold shadow-xl active:scale-95 transition-transform cursor-pointer border border-amber-400/40"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        style={{ touchAction: "manipulation" }}
        aria-label={t("categories.open", "Категорії")}
      >
        ☰ <span>{t("categories.menu", "Категорії")}</span>
      </button>

      {/* En: Backdrop overlay for the bottom sheet drawer */}
      {open && (
        <div
          className="fixed inset-0 bg-black/60 z-45 pointer-events-auto backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
      )}

      {/* En: Bottom sheet drawer containing the filtered list of category buttons */}
      {open && (
        <div
          className="fixed bottom-0 left-0 right-0 max-h-[75vh] bg-stone-900 text-stone-100 z-50 rounded-t-2xl shadow-2xl p-5 overflow-y-auto pointer-events-auto border-t border-stone-800"
          style={{ touchAction: "pan-y" }}
        >
          {/* Header with Segmented Control Switcher */}
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-stone-800 gap-2">
            <div className="inline-flex p-1 bg-stone-800/90 rounded-xl border border-stone-700/60 shrink-0">
              <button
                type="button"
                onClick={() => {
                  product.setActiveMenu?.("FOOD");
                }}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                  product.activeMenu === "FOOD"
                    ? "bg-amber-500 text-stone-950 shadow-sm"
                    : "text-stone-400 hover:text-stone-200"
                }`}
              >
                🍽️ {t("menuType.food", "Kuchnia")}
              </button>

              <button
                type="button"
                onClick={() => {
                  product.setActiveMenu?.("BAR");
                }}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                  product.activeMenu === "BAR"
                    ? "bg-amber-500 text-stone-950 shadow-sm"
                    : "text-stone-400 hover:text-stone-200"
                }`}
              >
                🍸 {t("menuType.bar", "Bar / Napoje")}
              </button>
            </div>

            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-stone-400 hover:text-stone-200 p-1 font-bold text-xl cursor-pointer"
            >
              ✕
            </button>
          </div>

          {/* Category List */}
          <ul className="flex flex-col gap-2.5 pb-4">
            {filteredCategories.map((type: IType) => {
              const localizedLabel = getLocalized(type.name, i18n.language);

              return (
                <li key={type.id} className="w-full">
                  <button
                    type="button"
                    className="w-full text-left px-4 py-3.5 rounded-xl bg-stone-800 text-stone-200 font-semibold active:bg-stone-700 transition-colors block border border-stone-700/50"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleScroll(type);
                    }}
                    style={{ touchAction: "manipulation" }}
                  >
                    {localizedLabel}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
});

export default MobileCategoryDrawer;
